import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { isNativeApp, isIOS } from "@/lib/nativeBridge";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";

const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

function loadAppleScript() {
  return new Promise((resolve, reject) => {
    if (window.AppleID) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[src="${APPLE_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Apple SDK load failed")));
      return;
    }
    const script = document.createElement("script");
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Apple SDK load failed"));
    document.head.appendChild(script);
  });
}

/**
 * useAppleSignIn — loads Apple's JS SDK on demand and exposes a signIn()
 * that fires the Apple popup. The button is hidden entirely when the
 * backend reports SIWA isn't configured (so we never render a button
 * that 503s on click).
 *
 * Returns:
 *   - enabled: boolean (backend has APPLE_* env vars set)
 *   - ready:   boolean (SDK loaded + initialized)
 *   - signIn:  async () => { identityToken, authorizationCode, email?, firstName?, lastName? }
 */
export function useAppleSignIn() {
  const nativeIOS = isNativeApp() && isIOS();
  const [enabled, setEnabled] = useState(nativeIOS);
  const [ready, setReady] = useState(nativeIOS);
  const [clientId, setClientId] = useState("");
  const [redirectUri, setRedirectUri] = useState("");

  useEffect(() => {
    // Native iOS uses the system ASAuthorization sheet via the
    // Capacitor plugin — no web SDK, no backend web credentials needed.
    if (nativeIOS) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/apple/config");
        if (cancelled) return;
        if (!data?.enabled) {
          setEnabled(false);
          return;
        }
        setEnabled(true);
        setClientId(data.client_id);
        setRedirectUri(data.redirect_uri);
        await loadAppleScript();
        if (cancelled) return;
        window.AppleID.auth.init({
          clientId: data.client_id,
          scope: "name email",
          redirectURI: data.redirect_uri,
          usePopup: true,
        });
        setReady(true);
      } catch (e) {
        // Silently disable — button won't render
        setEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    if (nativeIOS) {
      const { response } = await SignInWithApple.authorize({
        clientId: "com.armstrongk9.registry",
        redirectURI: "https://armstrong-k9.com",
        scopes: "email name",
      });
      return {
        identityToken: response?.identityToken,
        authorizationCode: response?.authorizationCode,
        email: response?.email,
        firstName: response?.givenName,
        lastName: response?.familyName,
      };
    }
    if (!ready) throw new Error("Apple Sign in not ready");
    const resp = await window.AppleID.auth.signIn();
    const auth = resp?.authorization || {};
    const user = resp?.user || {};
    return {
      identityToken: auth.id_token,
      authorizationCode: auth.code,
      email: user.email,
      firstName: user.name?.firstName,
      lastName: user.name?.lastName,
    };
  }, [ready, nativeIOS]);

  return { enabled, ready, signIn, clientId, redirectUri };
}
